import React from "react"
import PropTypes from "prop-types"
import { withRadium, DetectCompact } from "higher-order-components"
import { childParser } from "containers/utilities"
import bowser from "bowser"

/**
 * @param {Boolean} [alignMiddle=false] - Vertically and horizontally align contents of column.
 * @param {Boolean} [padding=false] - Apply padding to column.
 * @param {Number} [order] - Manually set order of column using [flex css order](https://developer.mozilla.org/en-US/docs/Web/CSS/order).
 * @param {'left'|'right'|'center'} [textAlign=left] - Text alignment.
 * @param {'left'|'right'|'center'} [textAlignMobile=left] - Text alignment on mobile.
 * @param {Boolean} [mobileAlwaysFirst=false] - Set column to first (in ColumnGroup) on mobile.
 * @param {Boolean} [flex=false] - Display columns as flex (used vertically fill column).
 * @param {boolean} gutter - Adds gutter spacing to column.
 * @param {boolean} [divider] - Adds dividing line to the right of column.
 * @param {boolean} minHeight - Give column a (effectively) minHeight. (used to fill children with no height)
 * @param {Array} components - Array of Components.
 * @returns {ReactElement} React Component
 */
const Column = ({
  minHeight,
  alignMiddle,
  flex,
  count,
  mobileAlwaysFirst,
  divider,
  gutter,
  padding,
  order,
  compact,
  components,
  children,
  styleName,
  textAlign,
  textAlignMobile
} = {}) => {
  let vertical,
    flexBasis,
    renderTextAlign,
    renderMobileAlwaysFirst
  if (compact) {
    renderTextAlign = textAlignMobile
    flexBasis = minHeight ? "300px" : "0"
    renderMobileAlwaysFirst = mobileAlwaysFirst
    vertical = true
  } else {
    renderTextAlign = textAlign
    // the .toFixed() - 2 part is for IE11, more info: https://github.com/philipwalton/flexbugs/issues/3
    flexBasis =
      bowser.ios && bowser.version < 10 ? "auto" : `${(100 / count).toFixed() - 2 || 100}%`
    renderMobileAlwaysFirst = false
    vertical = false
  }

  return (
    <div
      data-test="column"
      style={[
        // flex-basis: 100% doesn't work on Safari 9, replaced with 'auto instead. This can be revert when support for Safari 9 dropped.
        (!vertical || minHeight) && {
          flex: `1 ${minHeight ? 0 : 1} auto`
        },
        vertical && { flex: "1 1 auto" },
        styles.column(styleName),
        alignMiddle && { margin: "auto" },
        (minHeight || flex) && { display: "flex" },
        divider && styles.divider,
        { textAlign: renderTextAlign },
        order && { order: order },
        renderMobileAlwaysFirst && { order: -1 },
        { width: "100%" }
      ]}>
      {/* We need to separate padding to a different div as IE11 has a problem with flex and box-sizing interaction */}
      <div style={[{ padding: gutter ? "0 1em" : "0" }, padding && styles.padding]}>
        {childParser({ components, children })}
      </div>
    </div>
  )
}

Column.propTypes = {
  alignMiddle: PropTypes.bool,
  padding: PropTypes.bool,
  compact: PropTypes.bool,
  order: PropTypes.number,
  textAlign: PropTypes.oneOf(["left", "right", "center"]),
  textAlignMobile: PropTypes.oneOf(["left", "right", "center"]),
  mobileAlwaysFirst: PropTypes.bool,
  divider: PropTypes.bool,
  count: PropTypes.number,
  gutter: PropTypes.bool,
  minHeight: PropTypes.bool,
  flex: PropTypes.bool,
  components: PropTypes.array,
  styleName: PropTypes.oneOf(["dark", "light"])
}

Column.defaultProps = {
  textAlign: "left",
  textAlignMobile: "center",
  styleName: "light"
}

export default withRadium(DetectCompact())(Column)

const styles = {
  column: styleName => ({
    textAlign: "center",
    boxSizing: "border-box",
    backgroundColor: styleName === "dark" && "#101010"
  }),
  padding: {
    padding: "2.5em",
    "@media (min-width: 820px)": {
      padding: "4em"
    }
  },
  divider: {
    "@media (min-width: 820px)": {
      borderRight: "1px solid #e7e7e7"
    }
  }
}



// WEBPACK FOOTER //
// ./src/containers/column/index.jsx